import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Button, Modal, Form, ButtonGroup } from 'react-bootstrap';

import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GLOBALS from '../../utils/Globals';
import "../../App.css";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-theme-material.css";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-067893}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{nutrad}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{nutrad_portal}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{nutrad_portal}_need_to_be_licensed___{nutrad_portal}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{29_October_2025}____[v3]_[01]_MTc2MTY5NjAwMDAwMA==71e88be6064d0b1886823ed0ecdbe20c"
);

function CustomFieldManager() {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newField, setNewField] = useState({
    name: '',
    description: '',
    type: 'text',
    required: false,
    defaultValue: ''
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editField, setEditField] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteField, setDeleteField] = useState(null);

  const baseURLAddCustomFields = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLAddCustomFields
    : GLOBALS.DEV.baseURLAddCustomFields;

    const baseURLGetCustomFields = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLGetCustomFields
    : GLOBALS.DEV.baseURLGetCustomFields;

    const baseURLDeleteCustomFields = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLDeleteCustomFields
    : GLOBALS.DEV.baseURLDeleteCustomFields;

    const baseURLUpdateCustomField = process.env.REACT_APP_ENV === "PROD"
    ? GLOBALS.PROD.baseURLUpdateCustomField
    : GLOBALS.DEV.baseURLUpdateCustomField;

  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: "Field Name",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "description",
        headerName: "Description",
        sortable: true,
        filter: true,
        flex: 2,
      },
      {
        field: "type",
        headerName: "Field Type",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        field: "required",
        headerName: "Required",
        sortable: true,
        filter: true,
        flex: 1,
        cellRenderer: (params) => (params.value ? "Yes" : "No"),
      },
      {
        field: "defaultValue",
        headerName: "Default Value",
        sortable: true,
        filter: true,
        flex: 1,
      },
      {
        headerName: "Actions",
        flex: 1,
        cellRenderer: (params) => (
          <div className="d-flex gap-2"> {/* Using Bootstrap's gap utility */}
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleEdit(params.data)}
            >
              <i className="bi bi-pencil"></i> Edit
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeleteClick(params.data)}
            >
              <i className="bi bi-trash"></i> Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
    }),
    []
  );

  const gridOptions = useMemo(() => ({
    rowSelection: {
      type: 'multiple'
    }
  }), []);

  useEffect(() => {
    fetchCustomFields();
  }, []);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${baseURLGetCustomFields}`);
      setRowData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching custom fields:", error);
      toast.error("Failed to fetch custom fields", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${baseURLAddCustomFields}`, newField);
      toast.success("Custom field added successfully", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowAddModal(false);
      setNewField({
        name: "",
        description: "",
        type: "text",
        required: false,
        defaultValue: "",
      });
      fetchCustomFields();
    } catch (error) {
      console.error("Error adding custom field:", error);
      toast.error("Failed to add custom field", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleDeleteClick = (field) => {
    setDeleteField(field);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${baseURLDeleteCustomFields}/${deleteField._id}`);
      toast.success("Custom field deleted successfully", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowDeleteModal(false);
      setDeleteField(null);
      fetchCustomFields();
    } catch (error) {
      console.error("Error deleting custom field:", error);
      toast.error("Failed to delete custom field", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleEdit = (field) => {
    setEditField(field);
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${baseURLUpdateCustomField}/${editField._id}`, editField);
      toast.success("Custom field updated successfully", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowEditModal(false);
      setEditField(null);
      fetchCustomFields();
    } catch (error) {
      console.error("Error updating custom field:", error);
      toast.error("Failed to update custom field", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Custom Fields List Manager</h2>
        <Button variant="outline-primary" onClick={() => setShowAddModal(true)}>
          Add New Custom Field
        </Button>
      </div>

      <div
        className="ag-theme-material"
        style={{ height: "600px", width: "100%" }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          gridOptions={gridOptions}
        />
      </div>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Custom Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Field Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter field name"
                value={newField.name}
                onChange={(e) =>
                  setNewField({ ...newField, name: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter description"
                value={newField.description}
                onChange={(e) =>
                  setNewField({ ...newField, description: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Field Type</Form.Label>
              <Form.Select
                value={newField.type}
                onChange={(e) =>
                  setNewField({ ...newField, type: e.target.value })
                }
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="boolean">Boolean</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Required"
                checked={newField.required}
                onChange={(e) =>
                  setNewField({ ...newField, required: e.target.checked })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Default Value</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter default value"
                value={newField.defaultValue}
                onChange={(e) =>
                  setNewField({ ...newField, defaultValue: e.target.value })
                }
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
              <Button variant="outline-primary" type="submit">
                Add Field
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Custom Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editField && (
            <Form onSubmit={handleUpdate}>
              <Form.Group className="mb-3">
                <Form.Label>Field Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter field name"
                  value={editField.name}
                  onChange={(e) =>
                    setEditField({ ...editField, name: e.target.value })
                  }
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter description"
                  value={editField.description}
                  onChange={(e) =>
                    setEditField({ ...editField, description: e.target.value })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Field Type</Form.Label>
                <Form.Select
                  value={editField.type}
                  onChange={(e) =>
                    setEditField({ ...editField, type: e.target.value })
                  }
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Required"
                  checked={editField.required}
                  onChange={(e) =>
                    setEditField({ ...editField, required: e.target.checked })
                  }
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Default Value</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter default value"
                  value={editField.defaultValue}
                  onChange={(e) =>
                    setEditField({ ...editField, defaultValue: e.target.value })
                  }
                />
              </Form.Group>

              <div className="d-flex justify-content-end">
                <Button
                  variant="outline-secondary"
                  className="me-2"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </Button>
                <Button variant="outline-primary" type="submit">
                  Update Field
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      <Modal 
        show={showDeleteModal} 
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteField(null);
        }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center">
            <div className="text-danger me-3">
              <i className="bi bi-exclamation-triangle-fill fs-1"></i>
            </div>
            <div>
              <h5 className="mb-2">Delete Custom Field</h5>
              <p className="mb-0">
                Are you sure you want to delete the custom field "{deleteField?.name}"? 
                This action cannot be undone.
              </p>
              {deleteField?.description && (
                <p className="mb-0 text-muted">
                  <small>Description: {deleteField.description}</small>
                </p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteField(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="outline-danger" 
            onClick={handleDeleteConfirm}
          >
            Delete Field
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
}

export default CustomFieldManager;
